import type { InitCommandOptions } from '@cli-types/core-engine/commands.js'
import type { InitJsonContext } from '@cli-types/next-adapter/commands.js'
import { redactSecrets } from '@cli-utils/process.js'
import { installPromptCheckmarks, redirectStdoutToStderr } from '@cli-utils/terminal.js'
import { runInitCommandInternal } from './run-init-command-internal.js'
import { writeInitJson } from './write-init-json.js'

export async function runInitCommand(
  name: string | undefined,
  options: InitCommandOptions
): Promise<void> {
  // Both of these patch `process.stdout.write`, and each restore reinstates the
  // write that was current when it installed — so they must be installed and
  // restored in LIFO order. Checkmarks go on first: the JSON redirect is
  // restored mid-run (by `writeInitJson`), and if it sat underneath it would
  // reinstate a write from before the checkmark patch existed, silently
  // discarding it for the rest of the process.
  installPromptCheckmarks()

  const jsonContext: InitJsonContext = {
    restoreStdout: options.json ? redirectStdoutToStderr() : undefined,
    written: false
  }

  try {
    await runInitCommandInternal(name, options, jsonContext)
  } catch (error) {
    if (options.json && !jsonContext.written) {
      const message = error instanceof Error ? error.message : String(error)
      writeInitJson(jsonContext, {
        success: false,
        phase: 'initialization',
        error: {
          code: 'UNEXPECTED_INIT_ERROR',
          message: redactSecrets(message)
        }
      })
    }
    throw error
  } finally {
    jsonContext.restoreStdout?.()
  }
}
